/*
 * =============================================================================
 * File        : app.component.ts
 * Description : Main app component.
 * Author      : <AUTHOR_NAME>
 * Created     : <CREATION_DATE>
 * =============================================================================
 *
 * VERSION HISTORY
 * -----------------------------------------------------------------------------
 * Version |   Date     | Author              | Description (Commit message style)
 * --------|------------|---------------------|---------------------------------
 * 1.0.0   | 2026-03-26 | Martim Pintão       | Initial version
 * -----------------------------------------------------------------------------
 *
 * Notes:
 * - Please update the version and description on every meaningful change.
 * - Keep the format clean and aligned for readability.
 *
 * =============================================================================
 */

import {
  Component,
  NgZone,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  AfterViewInit,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterEvent,
  RouterLink,
} from '@angular/router';
import {
  Platform,
  AlertController,
  LoadingController,
  IonApp,
  IonRouterOutlet,
  IonToast,
  IonIcon,
} from '@ionic/angular/standalone';
import { SplashScreen } from '@capacitor/splash-screen';
//import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
//import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Badge } from '@awesome-cordova-plugins/badge/ngx';
import { Globalization } from '@awesome-cordova-plugins/globalization/ngx';
import { GoogleAnalyticsService } from 'src/app/providers/google-analytics.service';
import { UniqueDeviceID } from '@awesome-cordova-plugins/unique-device-id/ngx';
import OneSignal from 'onesignal-cordova-plugin';

import { Storage } from '@ionic/storage-angular';
import { v4 as uuidv4 } from 'uuid';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { common, environment, version } from '../environments/environment';
import { AgentsService } from './providers/crm-agent-provider';
import { AgentPostLoginService } from '../services/agent-post-login.service';
import { DeeplinkNavigationService } from '../services/deeplink-navigation.service';
import { isNativeOAuthCallbackUrl, OAuthService } from '../services/oauth.service';
import { EncriptionService } from './providers/encription.service';
import { GlobalDataService } from './providers/global-data.service';
import { AvatarService } from './providers/avatar.service';
import { GuestService } from './providers/guest.service';
import { AdsServicesService } from './providers/ads-services.service';
import { MessagingServicesProvider } from './providers/messaging-services';
/*
import {
  AppTrackingTransparency,
  AppTrackingStatusResponse,
} from 'capacitor-plugin-app-tracking-transparency';*/
import { ThemeService } from './providers/theme.service';
import lottie from 'lottie-web';
import { animation } from '@angular/animations';
import { SvgPreloadService } from './providers/svg-preload.service';
import { AdvertizingImpressionComponent } from './components/advertizing-impression/advertizing-impression.component';
import { AdvertizingPrizeComponent } from './components/advertizing-prize/advertizing-prize.component';
import { LanguageProvider } from './providers/language';
import { AuthService } from 'src/services/auth.service';
import { debugLog, registerLogTag } from 'src/utils/debug-log';
import { addIcons } from 'ionicons';
import {
  ribbonOutline,
  footstepsOutline,
  logInOutline,
  closeOutline,
  rocketOutline,
  arrowBack,
  checkmarkCircleOutline,
  ellipse,
  addCircle,
  checkmarkCircle,
  settings,
  thumbsDown,
  thumbsUp,
  people,
  heart,
  mail,
  star,
  starOutline,
  alert,
  logOut,
  add,
  personAdd,
  trash,
  share,
  handRightOutline,
  arrowUpCircle,
  eyeOutline,
  eyeOffOutline,
  logIn,
  shieldCheckmark,
  arrowBackOutline,
  ribbon,
  search,
  searchOutline,
  refreshOutline,
  colorWandOutline,
  send,
  chevronUpCircleOutline,
  chevronDownCircleOutline,
  logoBuffer,
  man,
  mailOpen,
  chatbox,
  ellipsisVerticalOutline,
  globe,
  logoFacebook,
  logoWhatsapp,
  removeCircle,
  gridOutline,
  options,
  checkboxOutline,
  squareOutline,
  grid,
  paperPlane,
  chatbubbles,
  chevronBackOutline,
  chevronForwardOutline,
  checkmarkDoneCircle,
  closeCircle,
  cardOutline,
  close,
  alertCircleOutline,
  pencilOutline,
  checkmarkOutline,
  informationCircleOutline,
  closeCircleOutline,
  paperPlaneOutline,
  chevronDownOutline,
  chevronUpOutline,
  sunnyOutline,
  moonOutline,
  fingerPrintOutline,
  duplicateOutline,
  listOutline,
} from 'ionicons/icons';
import { IonRouterLink } from '@ionic/angular/standalone';
import {
  NgIf,
  NgClass,
  NgStyle,
  NgFor,
  Location,
  AsyncPipe,
} from '@angular/common';
// António Carmo - 20251003
//-------- DeepLinks Implementation - Start -----//
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ConfirmPopupComponent } from './components/confirm-popup/confirm-popup.component';
import { ToastComponent } from './components/toast/toast.component';
import { NewsPopupComponent } from './components/news-popup/news-popup.component';
import { NewsPopupService } from './providers/news-popup.service';
import { PopupService } from './providers/popup.service';
import { StickerDetailOverlayService } from './providers/sticker-detail-overlay.service';
import { OpenBindersOverlayService } from './providers/open-binders-overlay.service';
import { TradeRequestDetailOverlayService } from './providers/trade-request-detail-overlay.service';
import { OpenSpinnersOverlayService } from './providers/open-spinners-overlay.service';
import { GetBindersOverlayService } from './providers/get-binders-overlay.service';
import { AddMultipleItemsOverlayService } from './providers/add-multiple-items-overlay.service';
import { AlbumOverlayService } from './providers/album-overlay.service';
import { RequestPhysicalCollectionOverlayService } from './providers/request-physical-collection-overlay.service';
import { HeaderService } from './providers/header.service';
import { TopPresentedModalBackService } from './providers/top-presented-modal-back.service';
import {
  V2DetailOverlayComponent,
  V2DetailVideoLoadState,
} from './components/v2-detail-overlay/v2-detail-overlay.component';
import { V2OpenBindersComponent } from './components/v2-open-binders/v2-open-binders.component';
import { V2TradeRequestDetailOverlayComponent } from './components/v2-trade-request-detail-overlay/v2-trade-request-detail-overlay.component';
import { V2OpenSpinnersComponent } from './components/v2-open-spinners/v2-open-spinners.component';
import { V2GetBindersComponent } from './components/v2-get-binders/v2-get-binders.component';
import { V2AddMultipleItemsComponent } from './components/v2-add-multiple-items/v2-add-multiple-items.component';
import { V2AlbumOverlayComponent } from './components/v2-album-overlay/v2-album-overlay.component';
import { V2RequestPhysicalCollectionComponent } from './components/v2-request-physical-collection/v2-request-physical-collection.component';
import { ConnectivityOverlayComponent } from './components/connectivity-overlay/connectivity-overlay.component';
import { ConnectivityService } from './providers/connectivity.service';
import { AppResumeService } from './providers/app-resume.service';
import { merge } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SideNavService } from './components/side-nav/side-nav.service';
import { SideNavPage } from './components/side-nav/side-nav.types';
//-------- DeepLinks Implementation - End -------//

registerLogTag('AppStartup', 'App startup', '#16a085');

type AppPage = SideNavPage;

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    AdvertizingImpressionComponent,
    AdvertizingPrizeComponent,
    NgIf,
    NgClass,
    SideNavComponent,
    IonRouterOutlet,
    TranslatePipe,
    AsyncPipe,
    ConfirmPopupComponent,
    IonToast,
    IonIcon,
    ToastComponent,
    NewsPopupComponent,
    V2DetailOverlayComponent,
    V2OpenBindersComponent,
    V2TradeRequestDetailOverlayComponent,
    V2OpenSpinnersComponent,
    V2GetBindersComponent,
    V2AddMultipleItemsComponent,
    V2AlbumOverlayComponent,
    V2RequestPhysicalCollectionComponent,
    ConnectivityOverlayComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChildren('lottieSpash') lottieContainers: QueryList<ElementRef>;
  @ViewChild('advertizingPrize') advertizingPrize: AdvertizingPrizeComponent;
  @ViewChild('advertizingImpression')
  advertizingImpression: AdvertizingImpressionComponent;
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet!: IonRouterOutlet;

  private readonly targetUrl = '/col-list/full-list';
  // EXACTLY these 4 pages (no extra segments after them)
  private readonly lockedExact = new RegExp(
    '^/user-collection/[^/]+/[^/]+/[^/]+/(col-items|trade-options|trade-requests|collection-rank)$'
  );
  // keep track of the current url (important for browser back)
  private currentUrl = '';

  splashScreen: boolean = true;
  splashFaded: boolean = false;

  /** Show theme selection news popup once on app open */
  showNewsPopup = false;

  /** Payload for the news popup, fed from API via NewsPopupService */
  newsPayload$ = this.newsPopupService.newsPayload$;

  /** Sticker detail overlay state – app-wide full-screen overlay */
  stickerOverlayState$ = this.stickerOverlayService.state$;

  /** Open binders overlay – app-wide full-screen */
  openBindersState$ = this.openBindersOverlayService.state$;

  /** Trade request detail overlay – app-wide full-screen */
  tradeRequestDetailOverlayState$ =
    this.tradeRequestDetailOverlayService.state$;

  /** Open spinners overlay – app-wide full-screen */
  openSpinnersState$ = this.openSpinnersOverlayService.state$;

  /** Get binders overlay – app-wide full-screen */
  getBindersState$ = this.getBindersOverlayService.state$;

  /** Add multiple items overlay – app-wide full-screen */
  addMultipleItemsState$ = this.addMultipleItemsOverlayService.state$;

  /** Album view overlay – app-wide full-screen */
  albumOverlayState$ = this.albumOverlayService.state$;

  /** Order physical album overlay – app-wide full-screen */
  requestPhysicalCollectionOverlayState$ =
    this.requestPhysicalCollectionOverlayService.state$;

  public selectedIndex = 0;

  agentId = '';
  displayingImpression = false;
  advertizementImage = '';
  advertizementDescription = '';
  advertizementURL = '';
  advertizementURLText = '';
  advertizementRemainingTime = 0;
  advertizementMinTime = 0;
  advertizementCfgImpressId = ''; // exme_mkt_cfg_col_impress_id
  advertizementImpressLogId = ''; //exme_mkt_col_impress_log_id

  displayingPrize = false;
  prizeImage = '';
  prizeDescription = '';
  prizeURL = '';
  prizeURLText = '';
  advertizementCfgPrizeId = ''; // exme_mkt_cfg_col_prize_id
  advertizementPrizeLogId = ''; //exme_mkt_col_prize_log_id

  userAvatar = '';
  userDisplayName = '';
  firstName = 'X';
  lastName = 'M';
  agentScore = 0;
  evaluations = 0;
  tradeRating = 0;

  userLang = 'en';
  key: any;
  loginType: any;
  agentByDevice: any;
  agentFromStorage: any;
  bitmask: number;

  isAnonymous = false;

  frase = '';

  public appPages: AppPage[] = [];
  onPauseSubscription: any;
  onResumeSubscription: any;

  isIOS: boolean;

  //Variable to control help square
  isSquareVisible: boolean = false;
  //  public menuHelp = [];
  i = 0;
  /*  currentMessage: {
      title: string;
      description: string;
      action: string;
    } | null = null;
  */
  //Showhelper variables
  isFirstLaunch: boolean = false;
  page = 'MenuPage';
  helperSelectedIndex: number;
  splashObserver: MutationObserver;
  removalObserver: MutationObserver;
  pollIntervalId: NodeJS.Timeout;
  constructor(
    public platform: Platform,
    //    private splashScreen: SplashScreen,
    //    private statusBar: StatusBar,
    //    private oneSignal: OneSignal,
    private badge: Badge,
    private router: Router,
    private globalization: Globalization,
    private gaService: GoogleAnalyticsService,
    private localStorage: Storage,
    private agentsService: AgentsService,
    private agentPostLogin: AgentPostLoginService,
    private deeplinkNav: DeeplinkNavigationService,
    private translate: TranslateService,
    private gds: GlobalDataService,
    private avatarService: AvatarService,
    private uniqueDeviceID: UniqueDeviceID,
    private sideNav: SideNavService,
    private crypto: EncriptionService,
    private guestSrvc: GuestService,
    private ads: AdsServicesService,
    private messagingServices: MessagingServicesProvider,
    private loadingCtrl: LoadingController,
    private theme: ThemeService,
    private svgPreloadService: SvgPreloadService,
    private alertController: AlertController,
    private langProvider: LanguageProvider,
    private authService: AuthService,
    private oauthService: OAuthService,
    private connectivityService: ConnectivityService,
    private appResumeService: AppResumeService,
    private newsPopupService: NewsPopupService,
    private popupService: PopupService,
    public stickerOverlayService: StickerDetailOverlayService,
    public openBindersOverlayService: OpenBindersOverlayService,
    public tradeRequestDetailOverlayService: TradeRequestDetailOverlayService,
    public openSpinnersOverlayService: OpenSpinnersOverlayService,
    public getBindersOverlayService: GetBindersOverlayService,
    public addMultipleItemsOverlayService: AddMultipleItemsOverlayService,
    public albumOverlayService: AlbumOverlayService,
    public requestPhysicalCollectionOverlayService: RequestPhysicalCollectionOverlayService,
    private headerService: HeaderService,
    private topPresentedModalBack: TopPresentedModalBackService,
    // António Carmo - 20251003
    // ------DeepLinks - Zone ---- Start
    private zone: NgZone,
    private location: Location // ------DeepLinks - Zone ---- End
  ) {
    // Track current URL after every successful navigation
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrl = this.cleanUrl(this.router.url);
        this.syncSideMenuForRoute(this.currentUrl);
        this.syncSelectedIndexForRoute(this.currentUrl);

        // iOS swipe: disabled on "locked" collection routes to avoid leaving by accident.
        // When a full-screen v2 overlay or the confirm dialog is open, re-enable so
        // edge-swipe can be handled in NavigationStart (dismiss, stay on the page).
        if (this.routerOutlet) {
          const locked = this.lockedExact.test(this.currentUrl);
          const anyOverlay = this.isAnyV2FullScreenOverlayOpen();
          this.routerOutlet.swipeGesture = !locked || anyOverlay;
        }
      });

    // BROWSER / iOS edge-swipe back: close any open v2 overlay first, then same URL.
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        const isBrowserBack = e.navigationTrigger === 'popstate';
        if (!isBrowserBack) {
          return;
        }

        if (this.tryCloseSideNav()) {
          setTimeout(() => {
            this.router.navigateByUrl(this.currentUrl, { replaceUrl: true });
          }, 0);
          return;
        }

        if (this.tryCloseTopV2FullScreenOverlay()) {
          setTimeout(() => {
            this.router.navigateByUrl(this.currentUrl, { replaceUrl: true });
          }, 0);
          return;
        }

        if (this.lockedExact.test(this.currentUrl)) {
          setTimeout(() => {
            this.router.navigateByUrl(this.targetUrl, { replaceUrl: true });
          }, 0);
        }
      });

    // ANDROID hardware back (and related): close the top v2 full-screen overlay first
    this.platform.backButton.subscribeWithPriority(9999, async () => {
      if (this.tryCloseSideNav()) {
        return;
      }

      if (this.tryCloseTopV2FullScreenOverlay()) {
        return;
      }

      const url = this.cleanUrl(this.router.url);

      if (this.lockedExact.test(url)) {
        await this.router.navigateByUrl(this.targetUrl, { replaceUrl: true });
        return;
      }

      this.location.back();
    });

    console.log('AppComponent constructor');
    this.startObserving();
    this.gds.publishdeviceCountry('PT');

    this.appPages = [
      {
        title: 'COLLECTIONS',
        url: '/col-list/full-list',
        icon: 'albums',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/My-Collections.svg',
      },
      {
        title: 'ADD_COLLECTION',
        url: '/add-collection',
        icon: 'add',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/Add-Collection.svg',
      },
      // {
      //   title: 'ADMIN_COLLECTION',
      //   url: '/col-admin',
      //   icon: 'paper-plane',
      //   badgeValue: null,
      //   hide: false,
      //   svg: '/assets/icon/New-Icons/Manage.svg',
      // },
      {
        title: 'USER_MESSAGES',
        url: '/chat',
        icon: 'heart',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/Messages.svg',
      },
      // {
      //   title: 'GROUPS',
      //   url: '/clubs',
      //   icon: 'archive',
      //   badgeValue: null,
      //   hide: false,
      //   svg: '/assets/icon/New-Icons/Groups.svg',
      // },
      {
        title: 'PREFERENCES_PAGE',
        url: '/preferences',
        icon: 'trash',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/Preferences.svg',
      },
      {
        title: 'APP_MESSAGES',
        url: '/news',
        icon: 'warning',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/News.svg',
      },
      {
        title: 'QR_CODE',
        url: '/qr-code',
        icon: 'heart',
        badgeValue: null,
        hide: true,
        svg: '/assets/icon/New-Icons/QR-Code.svg',
      },
      {
        title: 'HISTORY',
        url: '/history',
        icon: 'heart',
        badgeValue: null,
        hide: false,
        svg: '/assets/icon/New-Icons/History.svg',
      },
    ];
    this.showSplash();
    this.initializeApp();

    // Subscribe to news popup visibility changes
    this.newsPopupService.showNewsPopup$.subscribe((visible) => {
      this.showNewsPopup = visible;
    });

    this.wireSideNavCloseOnOverlayOpen();

    //    this.currentMessage = this.menuHelp[this.i];
    addIcons({
      footstepsOutline,
      fingerPrintOutline,
      logInOutline,
      closeOutline,
      ribbonOutline,
      rocketOutline,
      arrowBack,
      checkmarkCircleOutline,
      ellipse,
      addCircle,
      checkmarkCircle,
      settings,
      thumbsDown,
      thumbsUp,
      people,
      heart,
      mail,
      star,
      starOutline,
      alert,
      logOut,
      add,
      personAdd,
      trash,
      share,
      handRightOutline,
      arrowUpCircle,
      eyeOutline,
      eyeOffOutline,
      logIn,
      shieldCheckmark,
      arrowBackOutline,
      ribbon,
      search,
      searchOutline,
      refreshOutline,
      colorWandOutline,
      send,
      chevronUpCircleOutline,
      chevronDownCircleOutline,
      logoBuffer,
      man,
      mailOpen,
      chatbox,
      ellipsisVerticalOutline,
      globe,
      logoFacebook,
      logoWhatsapp,
      removeCircle,
      gridOutline,
      options,
      checkboxOutline,
      squareOutline,
      grid,
      paperPlane,
      chatbubbles,
      chevronBackOutline,
      chevronForwardOutline,
      checkmarkDoneCircle,
      closeCircle,
      cardOutline,
      close,
      alertCircleOutline,
      pencilOutline,
      checkmarkOutline,
      informationCircleOutline,
      closeCircleOutline,
      paperPlaneOutline,
      chevronDownOutline,
      chevronUpOutline,
      sunnyOutline,
      moonOutline,
      duplicateOutline,
      listOutline,
    });
  }

  isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  /** Dev theme toggle – only shown when !environment.production */
  showDevThemeToggle = !environment.production;

  /** Bound to the dev theme toggle; synced when theme is applied. */
  devThemeIsDark = document.body.classList.contains('dark');

  toggleDevTheme() {
    const next = this.devThemeIsDark ? 'light' : 'dark';
    this.applyTheme(next);
  }

  private applyTheme(mode: string | null): void {
    this.theme.chooseTheme(mode);
    this.syncDevThemeToggleState();
  }

  private syncDevThemeToggleState(): void {
    this.devThemeIsDark = document.body.classList.contains('dark');
  }

  async ngOnInit() {
    // live updates when OS theme changes
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        this.isDarkMode = e.matches;
      });

    this.svgPreloadService
      .preloadSvg('./assets/icon/Loader.json', 'loaderSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg(
        './assets/icon/video-play-icon-light.json',
        'videoPlayLightSvg'
      )
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/video-play-icon-dark.json', 'videoPlayDarkSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Stars.json', 'starsSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg(
        './assets/icon/video-play-icon-full-light.json',
        'videoPlayFullLightSvg'
      )
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Buy-Album.json', 'buyAlbumSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Pending_Payment.json', 'pendingPaymentSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Waiting-Dispatch.json', 'waitingDispatchSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Dispatched.json', 'dispatchedSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Received.json', 'receivedSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Wait.json', 'waitSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Cancelled.json', 'CancelledSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/On-Hold.json', 'OnHoldSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Didnt-Received.json', 'DidntReceivedSvg')
      .subscribe();
    this.svgPreloadService
      .preloadSvg('./assets/icon/Not-Available.json', 'NotAvailableSvg')
      .subscribe();

    await this.localStorage.create();
    this.theme.initFromStorage();
    const path = window.location.pathname.split('folder/')[1];
    if (path !== undefined) {
      console.log('Going in here');
      this.selectedIndex = this.appPages.findIndex(
        (page) => page.title.toLowerCase() === path.toLowerCase()
      );
    }

    setTimeout(() => {
      this.applyTheme(localStorage.getItem('theme'));
    }, 3000);

    this.isIOS = this.platform.is('ios');
  }

  // Close helper manually
  closeHelper() {
    this.isSquareVisible = false;
  }

  onSideNavNavigate(index: number) {
    this.sideNav.close();
    this.changeSelectedIndexAndNavigate(index);
  }

  onSideNavProfile() {
    this.sideNav.close();
    void this.router.navigate(['/preferences/profile']);
  }

  changeSelectedIndexAndNavigate(index: number) {
    this.selectedIndex = index;
    this.gds.clearStoredCollectionData();
    this.router.navigateByUrl(this.appPages[index].url);
  }

  toggleSquare() {
    this.isSquareVisible = !this.isSquareVisible;

    if (this.isSquareVisible) {
      this.helperSelectedIndex = -1;
      //      this.currentMessage = this.menuHelp[this.selectedIndex];

      this.unsetFirstLaunch();
    }
  }
  setFirstLaunch() {
    this.isFirstLaunch = true;
  }

  unsetFirstLaunch() {
    this.isFirstLaunch = false;
  }
  selectHelp(index: number) {
    this.helperSelectedIndex = index;
    //    this.currentMessage = this.menuHelp[index];
  }

  ngAfterViewInit() {
    // run once on startup too (important if app loads directly into a locked route)
    const url = this.cleanUrl(this.router.url);
    this.currentUrl = url;
    this.syncSideMenuForRoute(url);
    this.routerOutlet.swipeGesture = !this.lockedExact.test(url);

    this.splashScreenLoader();
    setTimeout(() => this.applyTheme(localStorage.getItem('theme')));
    // Fallback: if splash never ends (e.g. lottie fails or no splash on mobile), show popup after delay
    setTimeout(() => {
      this.splashScreen = false;
    }, 3200);
  }

  async showSplash() {
    await this.platform.ready();
    SplashScreen.hide();
    const lottie = (window as any).lottie;
    if (this.platform.is('ios')) {
      //if ( lottie!== undefined && lottie!= null && lottie.splashScreen!==undefined && lottie.splashScreen!==null){
      await lottie.splashscreen.hide();
      await lottie.splashscreen.show(
        'public/assets/icon/SpashScreen.json',
        false
      );
      lottie.splashscreen.on(
        'lottieAnimationEnd',
        (ev: Event | RouterEvent) => {
          if (Capacitor.isNativePlatform()) {
            StatusBar.setOverlaysWebView({ overlay: false });
          }
          this.applyTheme(localStorage.getItem('theme'));
          lottie.splashscreen.hide();
          this.log('status change status bar color');
          //StatusBar.setStyle({ style: Style.Dark });
          //StatusBar.setBackgroundColor({ color: '#4dfgfg'});

          if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
            StatusBar.setOverlaysWebView({ overlay: false });
            // StatusBar.setStyle({ style: Style.Light });
            StatusBar.show();
          }
        }
      );
      //}
    }
  }

  splashScreenLoader() {
    if (!this.lottieContainers?.length) {
      // No splash element (e.g. on mobile): end splash after short delay so theme popup can show
      setTimeout(() => this.startFadeOut(), 500);
      return;
    }
    this.lottieContainers.forEach((container) => {
      const animation = lottie.loadAnimation({
        container: container.nativeElement,
        path: './assets/icon/SpashScreen.json',
        renderer: 'svg',
        loop: false,
        autoplay: true,
      });

      const checkFrameRate = () => {
        if (animation.frameRate > 0) {
          console.log('Frame rate is valid, starting progress check');
          checkAnimationProgress();
        } else {
          console.log('Frame rate is not valid yet, checking again...');
          setTimeout(checkFrameRate, 100);
        }
      };

      const checkAnimationProgress = () => {
        const frameRate = animation.frameRate;
        const totalFrames = animation.totalFrames;

        // Check if the animation has reached the last frame
        if (animation.currentFrame >= totalFrames - 1) {
          console.log('Animation completed');
          this.startFadeOut();
        } else {
          // If not, check again after a short delay
          setTimeout(checkAnimationProgress, 1000 / frameRate);
        }
      };

      // Start checking the frame rate
      checkFrameRate();
    });
  }

  startFadeOut() {
    this.splashFaded = true; // Trigger the fade-out animation
    setTimeout(() => {
      this.splashScreen = false; // Hide the splash screen after the animation completes
      console.log(' status Splash screen faded out');
    }, 1000); // Match this delay with the duration of the CSS animation
  }

  logout(clear: boolean) {
    this.gds.logout(clear);
  }

  getUserKeyPassFromStorage(iteration: number) {
    this.gds.getUserKeyPassFromStorage().then(
      (key) => {
        this.bitmask += 1;
        console.log('APP COMP INIT- Got getUserKeyPassFromStorage=' + key);
        this.key = key;
      },
      (err) => {
        if (iteration < 6) {
          setTimeout(() => {
            this.getUserKeyPassFromStorage(iteration + 1);
          }, 100);
        } else {
          this.bitmask += 1;
        }
      }
    );
  }

  getUserLoginTypeFromStorage(iteration: number) {
    this.gds.getUserLoginTypeFromStorage().then(
      (tp) => {
        this.bitmask += 2;
        console.log('APP COMP INIT- Got getUserLoginTypeFromStorage=' + tp);
        this.loginType = tp;
      },
      (err) => {
        if (iteration < 6) {
          setTimeout(() => {
            this.getUserLoginTypeFromStorage(iteration + 1);
          }, 100);
        } else {
          this.bitmask += 2;
        }
      }
    );
  }

  async initializeAdMob() {
    await this.ads.initializeAdMob();
  }

  /** Load agent from CRM when we have an id; storage is only used to resolve id / offline fallback. */
  private async loadStoredOrSessionAgent(): Promise<void> {
    if (this.authService.isOAuthCallbackInProgress()) {
      debugLog(
        'AppStartup',
        'deferred loadStoredOrSessionAgent — OAuth token exchange on /login',
        'warn'
      );
      return;
    }

    let storedAgent: any = null;
    let agentId: string | null = null;

    try {
      storedAgent = await this.localStorage.get('agent');
      const storedRow =
        storedAgent != null && Array.isArray(storedAgent)
          ? storedAgent[0] ?? null
          : storedAgent;

      if (storedRow?.AGENT_ID != null) {
        agentId = String(storedRow.AGENT_ID);
      } else if (storedAgent != null) {
        console.log('Unexpected Agent from storage is:');
        console.log(storedAgent);
        this.gds.publishAgentLanguage(1);
      } else {
        console.log('no agent -- first login, or logged out');
      }
    } catch (err) {
      console.error('Error reading agent from Ionic Storage');
      console.error(err);
    }

    const sessionAgentId = await this.authService.bootstrapSession();
    if (sessionAgentId) {
      agentId = sessionAgentId;
      debugLog('AppStartup', `agentId=${agentId}`, 'success');
    } else {
      debugLog('AppStartup', `sessionAgentId=${sessionAgentId}`, 'warn');
    }

    if (agentId) {
      try {
        const ret: any = await this.agentsService.getCrmAgentById(agentId);
        debugLog('AppStartup', `ret=${ret}`, 'success');
        console.log(ret);
        const row = ret?.resource?.[0];
        if (row) {
          this.publishAgentFromCrmResource(ret.resource, row, 'CRM');
          return;
        }
      } catch (error) {
        console.error('Could not load agent from CRM', error);
      }
    }

    const storedRow =
      storedAgent != null && Array.isArray(storedAgent)
        ? storedAgent[0] ?? null
        : storedAgent;
    if (storedRow?.AGENT_ID != null) {
      this.publishAgentFromCrmResource(
        Array.isArray(storedAgent) ? storedAgent : [storedAgent],
        storedRow,
        'Ionic Storage fallback'
      );
      return;
    }

    if (!this.authService.hasStoredSession()) {
      this.authService.redirectToLogin(
        'no agent in storage and no OAuth session tokens'
      );
      return;
    }

    this.authService.redirectToLogin(
      'no agent after session bootstrap (tokens present but agent could not be loaded)'
    );
  }

  private publishAgentFromCrmResource(
    resource: any[],
    row: any,
    source: string
  ): void {
    this.gds.publishAgent(resource);
    this.gds.publishAgentId(row.AGENT_ID);
    this.gds.publishAgentEmail(row.EMAIL);
    this.gds.publishAgentLanguage(row.LANGUAGE_ID);
    if (row.LANGUAGE_ID != null) {
      void this.langProvider.evtChangeLang(row.LANGUAGE_ID);
    }
    debugLog(
      'AppStartup',
      `agent loaded from ${source} (AGENT_ID=${row.AGENT_ID})`,
      'success'
    );

    console.log(row);

    void this.agentPostLogin.navigateAfterAgentLoaded(row);

    // onboarding_completed
    // onboarding_step
    // profile_info_completed
  }

  initializeApp() {
    console.log('Initializing AppComponent');
    this.platform.ready().then(async () => {
      console.log('Platform is ready');

      // ACarmo 20250905 - garantir que se status bar estiver mal, volta a ser reposta ao fim de 5 seg
      // if (this.platform.is('ios')) {
      //   setTimeout(() => {
      //     StatusBar.setOverlaysWebView({ overlay: false });
      //   }, 10);

      // }
      if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
        // Hide status bar at start
        StatusBar.hide();
      }
      // ACarmo 20250905 - Fim de comentário

      // ACarmo 20250613 - adicionado if para plataforma android apenas
      if (this.platform.is('android')) {
        setTimeout(() => {
          const heightPx = (window as any).androidStatusBarHeight || 0;
          console.log('▶ Android statusBarHeight from resource:', heightPx);
          // document.documentElement.style.setProperty(
          //   '--statusbar-height',
          //   `${heightPx}px`
          // );
        }, 50);

        this.startPollingForStatusBarHeight();
      }
      // ACarmo 20250613 - Fim de comentário

      console.log(this.platform.platforms());
      if (version.buildPlatform === 'cordova' && Capacitor.isNativePlatform()) {
        // StatusBar.setOverlaysWebView({ overlay: false });
        // StatusBar.setStyle({ style: Style.Light });
        // // Color for splash screen
        // StatusBar.setBackgroundColor({ color: '#7af860'});
        // StatusBar.show();

        // instead of overlay: false …
        // António Carmo ... for IOS must be always false

        if (this.platform.is('android')) {
          StatusBar.setOverlaysWebView({ overlay: true });
        }

        StatusBar.setStyle({ style: Style.Light });
        StatusBar.show();
      }
      //SplashScreen.hide();
      this.initializeAdMob();
      //this.ads.evtShowBannerAdd();
      if (version.buildPlatform === 'cordova') {
        this.OneSignalInit();
      }
      if (this.platform.is('ios')) {
        //this.requestPermission();
      }

      console.log('subscribing Prize');
      this.gds.getExmePrize().subscribe(
        (exmePrize: any) => {
          console.log('retrieved exme prize data');
          if (exmePrize !== null) {
            console.log('retrieved exme impression data');
            console.log(exmePrize);
            this.prizeImage = exmePrize.prize_image;
            this.prizeDescription = exmePrize.prize_text;
            this.prizeURL = exmePrize.prize_url;
            this.prizeURLText = exmePrize.prize_url_text;
            this.advertizementCfgPrizeId = exmePrize.exme_mkt_cfg_col_prizes_id;
            this.advertizementPrizeLogId = exmePrize.exme_mkt_col_prize_log_id;
            this.displayingPrize = true;
            //this._changeDetection.detectChanges();
            //setTimeout(() => { this.decreaseAdvertizementTime(); },1000);
          }
        },
        (error) => {}
      );
      console.log('subscribing Impressions');
      this.gds.getExmeImpression().subscribe(
        (exmeImpression) => {
          console.log('retrieved exme impression data');
          console.log(exmeImpression);
          if (exmeImpression !== null) {
            this.advertizingImpression.advertizementImage =
              exmeImpression.advertizing_image;
            this.advertizingImpression.advertizementDescription =
              exmeImpression.advertizing_text;
            this.advertizingImpression.advertizementURL =
              exmeImpression.advertizing_url;
            this.advertizingImpression.advertizementURLText =
              exmeImpression.advertizing_url_text;
            this.advertizingImpression.advertizementRemainingTime =
              exmeImpression.max_seconds;
            this.advertizingImpression.advertizementMinTime =
              exmeImpression.min_seconds;
            this.advertizingImpression.advertizementCfgImpressId =
              exmeImpression.exme_mkt_cfg_col_impress_id;
            this.advertizingImpression.advertizementImpressLogId =
              exmeImpression.exme_mkt_col_impress_log_id;
            this.advertizingImpression.isVisible = true;
            this.advertizementImage = exmeImpression.advertizing_image;
            this.advertizementDescription = exmeImpression.advertizing_text;
            this.advertizementURL = exmeImpression.advertizing_url;
            this.advertizementURLText = exmeImpression.advertizing_url_text;
            this.advertizementRemainingTime = exmeImpression.max_seconds;
            this.advertizementMinTime = exmeImpression.min_seconds;
            this.advertizementCfgImpressId =
              exmeImpression.exme_mkt_cfg_col_impress_id;
            this.advertizementImpressLogId =
              exmeImpression.exme_mkt_col_impress_log_id;
            this.displayingImpression = true;
            //this._changeDetection.detectChanges();
            //setTimeout(() => { this.decreaseAdvertizementTime(); },1000);
            this.advertizingImpression.startTimeCounter();
          }
        },
        (error) => {}
      );

      this.loadDeviceId();
      this.getLocales();
      await this.connectivityService.init();
      void this.deeplinkNav.captureColdStartUrl().then(async () => {
        if (this.authService.hasStoredSession()) {
          await this.authService.bootstrapSession();
        }
        const online = await this.connectivityService.checkNow({
          showChecking: true,
        });
        if (online) {
          await this.loadStoredOrSessionAgent();
        }
      });
      console.log('Init Step 1');
      this.bitmask = 0;
      setTimeout(() => {
        this.getUserKeyPassFromStorage(1);
      }, 50);
      setTimeout(() => {
        this.getUserLoginTypeFromStorage(1);
      }, 70);
      this.gds.getdeviceId().subscribe(
        (uuid) => {
          this.bitmask += 4;
          console.log('APP COMP INIT- Got uniqueDeviceID=' + uuid);
        },
        (error) => {
          if (
            (error.status !== undefined &&
              error.status !== null &&
              error.status === 401) ||
            (error.status_code !== undefined &&
              error.status_code !== null &&
              error.status_code === 401)
          ) {
            this.gds.logout(false);
          }
        }
      );
      this.onPauseSubscription = this.platform.pause.subscribe(() => {
        // do stuff
      });

      this.onResumeSubscription = this.platform.resume.subscribe(() => {
        void this.appResumeService.run();
      });

      // António Carmo - 20251003
      // ------DeepLinks Listener ---- Start
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(() => {
          if (isNativeOAuthCallbackUrl(event.url)) {
            return;
          }
          if (this.deeplinkNav.registerFromAppUrl(event.url)) {
            void this.deeplinkNav.navigateNowIfPending();
          }
        });
      });
      // ------DeepLinks Listener ---- End

      console.log('Init Step Out');
    });
  }

  async loadDeviceId() {
    const uid = await this.gds.getdeviceIdFromStorage();

    if (uid === null) {
      this.uniqueDeviceID
        .get()
        .then((uuid: any) => {
          console.log('OBTIDO DEVICE ID - CORDOVA');
          this.gds.publishDeviceId(uuid);
        })
        .catch((error: any) => {
          console.log(error);
          console.log('OBTER DEVICE ID de NPM UUID');
          this.gds.publishDeviceId(uuidv4());
        });
    } else {
      console.log('OBTIDO DEVICE ID - DA STORAGE');
      this.gds.publishDeviceId(uid);
    }
  }

  getLocales() {
    this.translate.setDefaultLang('en');

    if (this.langProvider.hasCachedAppLanguage()) {
      const cachedCode = this.langProvider.getCachedLanguageCode();
      if (cachedCode) {
        this.userLang = cachedCode;
      }

      if (!Capacitor.isNativePlatform()) {
        this.applyBrowserCountryOnly();
        return;
      }

      this.globalization
        .getLocaleName()
        .then((res) => {
          console.log('Globalization Locale -> ' + res.value);
          this.gds.publishdeviceCountry(res.value);
        })
        .catch(() => {
          const locale = navigator.language.split('-')[1];
          console.log('Navigator locale -> ' + locale);
          this.gds.publishdeviceCountry(locale);
        });
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      this.applyBrowserLocales();
      return;
    }

    this.globalization
      .getPreferredLanguage()
      .then((res) => {
        console.log('Globalization User Lang -> ' + res.value);
        this.gds.publishdeviceLanguage(res.value);
        this.userLang = /(de|en|pt)/gi.test(res.value) ? res.value : 'en';
        this.translate.use(this.userLang.toLowerCase());
      })
      .catch(() => this.applyBrowserLocales());

    this.globalization
      .getLocaleName()
      .then((res) => {
        console.log('Globalization Locale -> ' + res.value);
        this.gds.publishdeviceCountry(res.value);
      })
      .catch(() => {
        const locale = navigator.language.split('-')[1];
        console.log('Navigator locale -> ' + locale);
        this.gds.publishdeviceCountry(locale);
      });
  }

  private applyBrowserLocales(): void {
    const lang = navigator.language.split('-')[0];
    console.log('Navigator language -> ' + lang);
    this.gds.publishdeviceLanguage(lang);
    this.userLang = /(de|en|pt)/gi.test(lang) ? lang : 'en';
    this.translate.use(this.userLang.toLowerCase());
    this.applyBrowserCountryOnly();
  }

  private applyBrowserCountryOnly(): void {
    const locale = navigator.language.split('-')[1];
    console.log('Navigator locale -> ' + locale);
    this.gds.publishdeviceCountry(locale);
  }

  startObserving() {
    console.log('-------------OBSERVING GLOBAL VARS--------------------');
    /* Teste do serviço para obter vars globais */
    this.gds.getObservable().subscribe(async (data: any) => {
      console.log('Data received Observable', data);
      if (
        data.refreshBadgesMenu !== undefined &&
        data.refreshAgentId !== undefined &&
        data.refreshAgentId !== null &&
        data.refreshAgentId !== ''
      ) {
        console.log(
          'Refreshing Badge Numbers on menu for agentId ' + data.refreshAgentId
        );

        try {
          const pendActions: any =
            await this.agentsService.getCrmAgentPendingActions(
              data.refreshAgentId
            );
          if (
            !(
              pendActions == null ||
              pendActions.resource === undefined ||
              pendActions.resource[0] == null
            )
          ) {
            const pendingActions = pendActions.resource[0];
            console.log('pendingActions');
            console.log(pendingActions);
            this.appPages[0].badgeValue =
              pendingActions.numPendingTradeRequest === 0
                ? null
                : pendingActions.numPendingTradeRequest;
            this.appPages[1].badgeValue = null;
            if (pendingActions.numPendingDelegations === 0) {
              this.appPages[2].badgeValue = null;
            } else {
              this.appPages[2].badgeValue =
                pendingActions.numPendingDelegations;
              this.appPages[2].hide = false;
            }
            this.appPages[4].badgeValue =
              pendingActions.numGroupsPending === 0
                ? null
                : pendingActions.numGroupsPending;
            console.log(
              'setting group value to ' + this.appPages[4].badgeValue
            );
            this.appPages[5].badgeValue = null;
            this.appPages[3].badgeValue =
              pendingActions.user_messages_pending === 0
                ? null
                : pendingActions.user_messages_pending;
            this.appPages[6].badgeValue = pendingActions.messages_pending
              ? '1'
              : null;
            const locTotalBadges =
              pendingActions.numPendingTradeRequest +
              pendingActions.numPendingDelegations +
              pendingActions.numGroupsPending +
              pendingActions.user_messages_pending +
              pendingActions.messages_pending;
            if (Capacitor.isNativePlatform()) {
              this.badge.set(locTotalBadges);
            }
            console.log('Menu Badges Updated to:' + locTotalBadges);
            if (
              pendingActions.terms_accept_version !==
              pendingActions.current_terms_version
            ) {
              this.popupService.open({
                title: this.translate.instant('TERMS_CHANGED_TITLE'),
                message: this.translate.instant('TERMS_CHANGED_SUBTITLE'),
                confirmText: this.translate.instant('ACCEPT_NEW_TERMS'),
                onConfirm: () => {
                  this.agentsService
                    .acceptNewTerms(
                      pendingActions.agent_id,
                      pendingActions.current_terms_version
                    )
                    .then(() => console.log('Success Updating Terms'))
                    .catch((error) => {
                      console.log('Error Updating Terms');
                      console.log(error);
                    });
                },
                confirmTextSecond: this.translate.instant('CHECK_NEW_TERMS'),
                onConfirmSecond: () => {
                  this.router.navigateByUrl('/preferences/terms-of-use/en');
                },
              });
            }
          }
        } catch (error) {
          console.log('error is:');
          console.log(error);
          console.log(error.status);
          console.log(error.status_code);
          console.log(error.error);
          if (
            error.status !== undefined &&
            error.status != null &&
            error.status === 401
          ) {
            console.log('In if...');
            this.gds.logout(false);
          }
          this.appPages[0].badgeValue = null;
          this.appPages[1].badgeValue = null;
          this.appPages[2].badgeValue = null;
          this.appPages[3].badgeValue = null;
          this.appPages[4].badgeValue = null;
          this.appPages[5].badgeValue = null;
          this.appPages[6].badgeValue = null;
          //          this.appPages[5].badgeValue = null;
          if (Capacitor.isNativePlatform()) {
            this.badge.clear();
          }
        }
      } else {
        console.log('Observable Data is undefined');
      }
    });

    this.gds.getuserAvatar().subscribe((data) => {
      void this.resolveUserAvatar(data);
    });

    this.gds.getAgent().subscribe((agent) => {
      this.userDisplayName = this.formatUserDisplayName(agent);
    });

    this.gds.getUserTradeRating().subscribe((data) => {
      this.tradeRating = data;
      console.log('APP COMPONENT GET USER TRADE RATING RETURNED:' + data);
    });

    this.gds.getNumEvaluations().subscribe((data) => {
      this.evaluations = data;
    });

    this.gds.getdeviceLanguage().subscribe((data: any) => {
      console.log('Reload do Menu na lingua certa....');
      console.log('loading menu for: ' + data);
      if (data.lang !== undefined) {
        this.translate.use(data.lang.toLowerCase());
      } else {
        if (data !== undefined && data != null) {
          this.translate.use(data.toLowerCase());
        } else {
          console.log('Não Temos ainda lingua definida....');
        }
      }
    });

    this.gds.getAgentFromStorage().then((agent: any) => {
      console.log('Storage Returned');
      console.log(agent);
      if (agent !== undefined && agent !== null) {
        if (Array.isArray(agent)) {
          // IS ARRAY --> CONSIDER ELEMENT [0]
          this.gds.loadAgent(agent[0]);
        } else {
          // IS NOT ARRAY -> use value
          this.gds.loadAgent(agent);
        }
      } else {
        //this.publishAgent(null);
        console.log('no agent -- first login, or logged out');
      }
    });

    this.gds.getdeviceCountry().subscribe((data) => {
      console.log('Device Country OBSERVABLE', data);
    });

    this.gds.getdeviceLanguage().subscribe((data) => {
      console.log('Device Language OBSERVABLE', data);
    });

    this.gds.getdeviceId().subscribe((data) => {
      console.log('Device Id OBSERVABLE', data);
    });

    this.gds.getAgentId().subscribe((data) => {
      console.log('Agent Id  OBSERVABLE', data);
      this.agentId = data;
    });

    this.gds.getagentEmail().subscribe((data) => {
      console.log('Agent Email OBSERVABLE', data);
      if (data === 'ExMEAnonymousAccount@exme.club') {
        this.isAnonymous = true;
      } else {
        this.isAnonymous = false;
      }
    });

    this.gds.getBadges().subscribe((data) => {
      for (const i of [0, 1, 2, 3, 4, 5, 6]) {
        if (this.appPages[i] !== undefined) {
          this.appPages[i].badgeValue = data[i];
        }
      }
    });
  }

  // Call this function when your app starts
  OneSignalInit(): void {
    // Uncomment to set OneSignal device logging to VERBOSE
    // OneSignal.setLogLevel(6, 0);
    if (this.platform.is('cordova')) {
      // NOTE: Update the setAppId value below with your OneSignal AppId.
      OneSignal.initialize('aea12ae3-de12-44cb-9f0e-119ba39fcb82');
      OneSignal.Notifications.addEventListener('click', function (jsonData) {
        console.log('notificationOpenedCallback: ' + JSON.stringify(jsonData));
        this.badge.increase(1);
      });

      // Prompts the user for notification permissions.
      //    * Since this shows a generic native prompt, we recommend instead using an In-App Message to prompt for notification permission (See step 7) to better communicate to your users what notifications they will get.
      // OneSignal.Notifications.requestPermission(true);
    }
  }

  initializeGoogleAnalytics() {
    void this.gaService.trackPageView('application_initialized');
  }

  async getUserSession(sessionCredentials: any) {
    try {
      const session: any = await this.agentsService.getUserSession(
        sessionCredentials
      );
      console.log('-------Step 2--------');
      console.log(session);
      void this.gaService.trackEvent('login_page', 'login', 'success', 1);
      this.gds.publishUserSession(session);
      // ver se é suficiente ou se precisamos voltar a "carregar" o agente na storage
      console.log('Navigating to col-list on function getUserSession');
      this.router.navigate(['/col-list']);
    } catch (error) {
      console.log('Navigating to login on function getUserSession');
      this.router.navigate(['/login']);
    }
  }

  preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  log(string: any) {
    console.log(string);
  }

  async setLanguage(languageId: number) {
    let language: string;

    // this.langProvider.getLanguageFromId(languageId).subscribe(
    //   (resp: any) => {
    //     console.log(resp);
    //     if (
    //       resp.resource === undefined ||
    //       resp.resource[0] === undefined ||
    //       resp.resource[0].language_code === undefined
    //     ) {
    //       language = 'en'.toLowerCase();
    //     } else {
    //       language = resp.resource[0].language_code.toLowerCase();
    //     }
    //     this.translate.use(language);
    //   },
    //   (error) => {
    //     this.translate.use('en');
    //   }
    // );

    try {
      const resp: any = await this.langProvider.getLanguageFromId(languageId);
      console.log(resp);
      if (
        resp.resource === undefined ||
        resp.resource[0] === undefined ||
        resp.resource[0].language_code === undefined
      ) {
        language = 'en'.toLowerCase();
      } else {
        language = resp.resource[0].language_code.toLowerCase();
      }
      this.translate.use(language);
    } catch (error) {
      this.translate.use('en');
    }
  }

  private startPollingForStatusBarHeight() {
    console.log('▶ startPollingForStatusBarHeight() called');
    console.log('version.buildPlatform:', version.buildPlatform);
    if (version.buildPlatform == 'cordova' && Capacitor.isNativePlatform()) {
      this.pollIntervalId = setInterval(async () => {
        try {
          const info: any = await StatusBar.getInfo();
          const h = info?.height || 0;
          console.log('• status Polled status-bar height =', h);
          if (h > 0) {
            // We got a real height—apply it and stop polling:
            document.documentElement.style.setProperty(
              '--statusbar-height',
              `${h}px`
            );
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
            console.log('✔ Applied status-bar padding:', h, 'px');
          }
        } catch (e) {
          // If getInfo() throws, ignore and try again next interval
          console.warn('⚠ StatusBar.getInfo() threw, will retry', e);
        }
      }, 16);
    }
  }

  private cleanUrl(url: string) {
    return url.split('?')[0].replace(/\/+$/, '');
  }

  /** Side nav trigger off on auth/onboarding only. */
  private readonly menuDisabledRoute =
    /^\/(login|signup|onboarding)(\/|$)/;

  private syncSideMenuForRoute(url: string): void {
    const shouldDisableMenu = this.menuDisabledRoute.test(url);
    this.sideNav.setEnabled(!shouldDisableMenu);
  }

  private syncSelectedIndexForRoute(url: string): void {
    let bestIndex = -1;
    let bestLength = -1;
    for (let i = 0; i < this.appPages.length; i++) {
      const page = this.appPages[i];
      if (page.hide) {
        continue;
      }
      const base = page.url.split('?')[0];
      if (url === base || url.startsWith(`${base}/`)) {
        if (base.length > bestLength) {
          bestLength = base.length;
          bestIndex = i;
        }
      }
    }
    if (bestIndex >= 0) {
      this.selectedIndex = bestIndex;
    }
  }

  private tryCloseSideNav(): boolean {
    if (this.sideNav.isPersistent) {
      return false;
    }
    if (this.sideNav.isOpen) {
      this.sideNav.close();
      return true;
    }
    return false;
  }

  private wireSideNavCloseOnOverlayOpen(): void {
    merge(
      this.stickerOverlayState$.pipe(map((s) => s.isOpen)),
      this.openBindersState$.pipe(map((s) => s.isOpen)),
      this.tradeRequestDetailOverlayState$.pipe(map((s) => s.isOpen)),
      this.openSpinnersState$.pipe(map((s) => s.isOpen)),
      this.getBindersState$.pipe(map((s) => s.isOpen)),
      this.addMultipleItemsState$.pipe(map((s) => s.isOpen)),
      this.albumOverlayState$.pipe(map((s) => s.isOpen)),
      this.requestPhysicalCollectionOverlayState$.pipe(map((s) => s.isOpen)),
      this.newsPopupService.showNewsPopup$
    )
      .pipe(
        map((open) => !!open),
        distinctUntilChanged(),
        filter((open) => open)
      )
      .subscribe(() => {
        this.sideNav.close();
      });
  }

  private isAnyV2FullScreenOverlayOpen(): boolean {
    return (
      this.popupService.isOpen ||
      this.topPresentedModalBack.isRegistered ||
      !!this.headerService.get('showManage') ||
      this.stickerOverlayService.isOpen ||
      this.requestPhysicalCollectionOverlayService.isOpen ||
      this.getBindersOverlayService.isOpen ||
      this.openSpinnersOverlayService.isOpen ||
      this.albumOverlayService.isOpen ||
      this.addMultipleItemsOverlayService.isOpen ||
      this.openBindersOverlayService.isOpen ||
      this.tradeRequestDetailOverlayService.isOpen
    );
  }

  /**
   * Picks the topmost v2 full-screen overlay (z-index / stacking order) and dismisses
   * it the same way as the in-UI back/close. Returns true if an overlay was closed.
   */
  private tryCloseTopV2FullScreenOverlay(): boolean {
    if (this.popupService.isOpen) {
      this.popupService.requestHardwareBackClose();
      return true;
    }
    if (this.topPresentedModalBack.tryDismiss()) {
      return true;
    }
    if (this.stickerOverlayService.isOpen) {
      this.handleStickerOverlayHardwareBack();
      return true;
    }
    if (this.tradeRequestDetailOverlayService.isOpen) {
      this.onTradeRequestDetailOverlayClosed();
      return true;
    }
    if (this.requestPhysicalCollectionOverlayService.isOpen) {
      this.onRequestPhysicalCollectionOverlayClosed();
      return true;
    }
    if (this.getBindersOverlayService.isOpen) {
      this.onGetBindersClosed();
      return true;
    }
    if (this.openSpinnersOverlayService.isOpen) {
      this.onOpenSpinnersClosed();
      return true;
    }
    if (this.albumOverlayService.isOpen) {
      this.albumOverlayService.requestHardwareBackClose();
      return true;
    }
    if (this.addMultipleItemsOverlayService.isOpen) {
      this.onAddMultipleItemsClosed();
      return true;
    }
    if (this.openBindersOverlayService.isOpen) {
      this.onOpenBindersClosed();
      return true;
    }
    if (this.headerService.get('showManage')) {
      this.headerService.setConfig({ showManage: false });
      return true;
    }
    return false;
  }

  /**
   * Sticker overlay is open: hardware/browser back should not jump to col-list.
   * Video view → back to slides; slide view → close overlay (same as header back / X).
   */
  private handleStickerOverlayHardwareBack(): void {
    if (this.stickerOverlayService.viewMode === 'videoView') {
      this.stickerOverlayService.callbacks?.onVideoBack?.();
    } else {
      this.stickerOverlayService.callbacks?.onClosed?.();
      this.stickerOverlayService.close();
    }
  }

  onNewsPopupClosed() {
    this.newsPopupService.closeNewsPopup();
  }

  onStickerOverlayClosed() {
    this.stickerOverlayService.callbacks?.onClosed?.();
    this.stickerOverlayService.close();
  }

  /** Payload matches `ModalController.dismiss(payload)` → parent reads `modalData.data`. */
  onOpenBindersClosed(payload?: any) {
    this.openBindersOverlayService.callbacks?.onDismiss?.({ data: payload });
    this.openBindersOverlayService.close();
  }

  onTradeRequestDetailOverlayClosed() {
    this.tradeRequestDetailOverlayService.callbacks?.onDismiss?.();
    this.tradeRequestDetailOverlayService.close();
  }

  onTradeRequestDetailAction(action: 'accept' | 'decline') {
    const requestId = String(
      this.tradeRequestDetailOverlayService.currentState.request
        ?.trade_request_id ?? ''
    );
    this.tradeRequestDetailOverlayService.callbacks?.onActionComplete?.({
      action,
      requestId,
    });
  }

  onOpenSpinnersPresentV3Cards(payload?: any) {
    this.openSpinnersOverlayService.callbacks?.onPresentV3Cards?.({ data: payload });
  }

  onOpenSpinnersClosed(payload?: any) {
    this.openSpinnersOverlayService.callbacks?.onDismiss?.({ data: payload });
    this.openSpinnersOverlayService.close();
  }

  onGetBindersClosed(payload?: any) {
    this.getBindersOverlayService.callbacks?.onDismiss?.({ data: payload });
    this.getBindersOverlayService.close();
  }

  onAddMultipleItemsClosed(payload?: any) {
    this.addMultipleItemsOverlayService.callbacks?.onDismiss?.({ data: payload });
    this.addMultipleItemsOverlayService.close();
  }

  onAlbumOverlayClosed(result: { activePageIndex: number }) {
    this.albumOverlayService.callbacks?.onDismiss?.(result);
    this.albumOverlayService.close();
  }

  onRequestPhysicalCollectionOverlayClosed(payload?: any) {
    this.requestPhysicalCollectionOverlayService.callbacks?.onDismiss?.({
      data: payload,
    });
    this.requestPhysicalCollectionOverlayService.close();
  }

  onStickerOverlayItemChange(payload: { index: number; item: any }) {
    this.stickerOverlayService.callbacks?.onItemChange?.(payload);
  }

  onStickerOverlayPlayVideo(payload: { index: number; item: any }) {
    this.stickerOverlayService.callbacks?.onPlayVideo?.(payload);
  }

  onStickerOverlaySectionChange(dir: 'next' | 'prev') {
    this.stickerOverlayService.callbacks?.onSectionChangeRequest?.(dir);
  }

  onStickerOverlayVideoEnded() {
    this.stickerOverlayService.callbacks?.onVideoEnded?.();
  }

  onStickerOverlayFooterAction(payload: { action: string; item: any }) {
    this.stickerOverlayService.callbacks?.onFooterAction?.(payload);
  }

  onStickerOverlayVideoLoadState(payload: { state: V2DetailVideoLoadState }) {
    this.stickerOverlayService.update({
      hasVideoLoaded: payload.state === 'ready',
    });
    this.stickerOverlayService.callbacks?.onVideoLoadState?.(payload);
  }

  /** Header chevron in video view — return to slide view without closing overlay */
  onStickerOverlayVideoBack() {
    this.stickerOverlayService.callbacks?.onVideoBack?.();
  }

  onStickerOverlayOpenAlbum(payload: { itemId: string }) {
    const id = String(payload?.itemId ?? '');
    if (!id) {
      return;
    }
    if (this.stickerOverlayService.callbacks?.onOpenAlbum) {
      this.stickerOverlayService.callbacks.onOpenAlbum({ itemId: id });
      return;
    }
    // Route changed: items page (which registers onOpenAlbum) may be gone, but the
    // album overlay can still be open under the detail overlay — still jump the album.
    if (this.albumOverlayService.isOpen) {
      this.albumOverlayService.patchIfOpen({ focusItemId: id });
    }
  }

  private async resolveUserAvatar(data: string | null | undefined): Promise<void> {
    if (!data) {
      this.userAvatar = '';
      return;
    }

    this.userAvatar = await this.avatarService.getAvatar(data);
  }

  private formatUserDisplayName(agent: any): string {
    if (!agent) {
      return '';
    }
    const first = (agent.FIRST_NAME ?? '').trim();
    const last = (agent.LAST_NAME ?? '').trim();
    return [first, last].filter(Boolean).join(' ').trim();
  }
}
